<template>
    <div>
        <div class="app-title">
            <em-view-title :crudState="$_emWorkflowListMixin_pathType"></em-view-title>
        </div>
        <section class="app-content">
            <div id="accordionFilter" class="content" v-menu-permission="'list'">
                <div class="card">
                    <div id="collapseOne" class="collapse show" aria-labelledby="headingOne"
                         data-parent="#accordionFilter">
                        <div class="card-body">
                            <form>
                                <em-filter :entity="modelName" :query-api="$_emWorkflowListMixin_queryApi"
                                           ref="emListFilter"
                                           @showEntityModalSelect="$_emCommonModalMixin_showModalSelectorByFilter"></em-filter>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <div id="emArchFixedAssetChangeResultTable" class="content">
                <div class="card my-1">
                    <div id="collapseTwo" class="collapse show" aria-labelledby="headingTwo"
                         data-parent="#accordionResult">
                        <div class="card-body">
                            <em-table :entity="modelName" inlineActionsDisabled
                                      :query-api="$_emWorkflowListMixin_queryApi"
                                      :ref="modelName+'Table'"
                                      editDisable
                                      with-custom-row-actions-first-column>
                                <template slot="left-header">
                                    <b-button-group>
                                        <em-button type="add"
                                                   v-bind:class="'btn btn-outline-primary btn-sm'"
                                                   custom-label="label.em.asset.emArchFixedAssetChange.apply"
                                                   custom-icon="fa fa-fw fa-lg fa-plus"
                                                   show-name
                                                   @click="goApply">
                                        </em-button>
                                        <em-button type="search"
                                                   v-if="isTypeTodo"
                                                   v-bind:class="'btn btn-outline-primary btn-sm'"
                                                   :customLabel="isClaim ? '认领' : '待办'"
                                                   :custom-icon="'fa ' + (isClaim ? 'fa-toggle-on' : 'fa-toggle-off')"
                                                   :busy="emWorkflowListLoading"
                                                   show-name
                                                   @click="switchClaim">
                                        </em-button>
                                    </b-button-group>
                                </template>
                                <template slot="customRowActionsFirstColumn" slot-scope="customRowScope">
                                    <b-button-group>
                                        <em-button type="task"
                                                   :busy="claimLoading"
                                                   v-bind:class="'btn btn-outline-primary btn-sm'"
                                                   :custom-label="isClaim ? '认领' : '处理'"
                                                   @click="handleExecute(customRowScope.row)">
                                        </em-button>
                                        <em-button type="edit"
                                                   v-if="allowModify(customRowScope.row)"
                                                   v-bind:class="'btn btn-outline-primary btn-sm'"
                                                   custom-label="label.em.asset.emArchFixedAssetChange.modify"
                                                   custom-icon="fa fa-fw fa-pencil-alt"
                                                   @click="goModify(customRowScope.row)">
                                        </em-button>
                                    </b-button-group>
                                </template>
                            </em-table>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <em-modal-entity-selector ref="emModalEntitySelector"></em-modal-entity-selector>
    </div>
</template>

<script>
// 固定资产变更 | 待办 / 列表（验收 #9 的待办部分）
// Author Liang Gangqiang
// Date 2026-09-07
//
// 与出门证同款：emWorkflowListMixin 按 pathType 决定查的是待办（todo）、认领（claim）还是全量（list）。
// 但本页只承担「待办 + 认领」两种视图（全量查询走 EmArchFixedAssetChangeQueryView），
// 所以下面把 pathType 钉死成 todo，见 computed.pathType 的注释。
//
// 注意两类节点的可见位置不同，这是全产品的既有约定，不是本页的特例：
//   assignee 型（task_change_init）      → 后端 getTodoTask 按 taskAssignee 查，直接进「待办」；
//   candidateUsers 型（两个经理节点 + 财务节点）
//                                        → 未认领时 assignee 为空，getTodoTask 查不到它，
//                                          只在 getCanBeClaimTask（按 taskCandidateUser 查）里，
//                                          必须先用左上角的「认领 / 待办」开关切到认领页认领，
//                                          认领后 assignee 落到本人，才会出现在待办并能打开审批表单
//                                          （Task 页的 isShowTaskComplete 只认 pathType=todo）。
// 通知邮件 / 站内信的深链会带 toClaim=true（EmArchAssetWorkflowNotifier），ClaimTaskMixins 据此
// 直接落在认领页，所以从通知点进来的人不需要自己切开关。
import emWorkflowListMixin from "@/mixin/em/workflow/list";
import claimTaskMixins from "@/components/workflow/mixin/ClaimTaskMixins";

export default {
    name: "emArchFixedAssetChangeWorkflowListView",
    mixins: [emWorkflowListMixin, claimTaskMixins],
    computed: {
        modelName() {
            return this.$_emRouterHelperMixin_entityName
        },

        /**
         * 覆写 ClaimTaskMixins.pathType：不管路由带的 pathType 是什么，本页一律按「待办」处理。
         *
         * 为什么：左侧菜单跳转时会把菜单的 crudCode 原样塞进 ?pathType=
         * （EmSidebar3Level#routeTo / EmSidebar 的 router-link），本页菜单当初按 crudCode=list 注册，
         * 于是从菜单进来是 pathType=list → isTypeTodo=false →「认领 / 待办」开关和「修改」按钮都不渲染，
         * 「处理」进 Task 页也只读（Task 页只在 pathType=todo 时显示提交区）。
         * 而铃铛 / 通知深链和发起页保存后的跳转都带 pathType=todo，所以那几条路是好的。
         * 菜单数据该改成 crudCode=todo，但本页不应再依赖它：在这里归一，菜单配成什么都能用。
         */
        pathType() {
            return 'todo';
        },

        /**
         * 同步覆写 ClaimTaskMixins 的同名 computed：它读的是 $route.query.pathType，
         * 不跟着改的话 emWorkflowListMixin 查询用的 workflowQueryTypeFilter 仍会是 list。
         */
        $_emRouterHelperMixin_pathType() {
            return this.isClaim ? 'claim' : this.pathType;
        }
    },
    methods: {
        /**
         * 只有 task_change_init 允许修改（EM_ACT_PROCESS_STATUS.allow_modify=true）。
         * 该列由工作流注册表提供，行数据缺失时按「不可改」处理，不要让按钮凭空出现。
         */
        allowModify(row) {
            return this.isTypeTodo && !this.isClaim && row && row.statusAllowModify === true;
        },

        goApply() {
            this.$router.push({path: '/em/asset/emArchFixedAssetChangeEdit'});
        },

        goModify(row) {
            this.$router.push({
                path: '/em/asset/emArchFixedAssetChangeEdit',
                query: {id: row.id}
            });
        },
    }
};
</script>
